/**
 * features/expenses/ExpensesPage.tsx
 */

import { useState } from 'react';
import { Pencil, Plus, Search, Trash2, Wallet } from 'lucide-react';
import { formatDate, formatMoney } from '@ft/core';
import { useDebounced } from '~/lib/use-debounced';
import { ApiError } from '~/lib/api';
import type { Expense } from '~/lib/types';
import { PageHeader, Pagination, Toolbar } from '~/ui/layout';
import { Button, Card, EmptyState, Input, Select, TableSkeleton } from '~/ui/primitives';
import { ConfirmDialog, useToast } from '~/ui/overlays';
import { useDeleteExpense, useExpenseCategories, useExpenses } from './hooks';
import { ExpenseFormDialog } from './ExpenseFormDialog';

const PAGE_SIZE = 15;

export function ExpensesPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const search = useDebounced(searchInput);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [deleting, setDeleting] = useState<Expense | null>(null);

  const { data, isLoading, isFetching } = useExpenses({
    page,
    pageSize: PAGE_SIZE,
    search,
    categoryId: categoryId || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });
  const { data: categories } = useExpenseCategories();
  const deleteExpense = useDeleteExpense();
  const toast = useToast();

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteExpense.mutateAsync(deleting.id);
      toast.success('ลบค่าใช้จ่ายแล้ว');
      setDeleting(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div>
      <PageHeader
        title="ค่าใช้จ่าย"
        description="บันทึกรายจ่ายและภาษีซื้อของธุรกิจ"
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus size={16} />
            บันทึกค่าใช้จ่าย
          </Button>
        }
      />

      <Card>
        <Toolbar className="flex-wrap gap-2">
          <div className="relative w-full max-w-xs">
            <Search size={15} className="absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-subtle" aria-hidden />
            <Input
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                setPage(1);
              }}
              placeholder="ค้นหาชื่อรายการ, ผู้ขาย"
              className="pl-8"
            />
          </div>

          <Select
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setPage(1);
            }}
            className="w-44"
          >
            <option value="">ทุกหมวดหมู่</option>
            {categories?.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>

          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(1);
            }}
            className="w-40"
            aria-label="จากวันที่"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(1);
            }}
            className="w-40"
            aria-label="ถึงวันที่"
          />

          {isFetching && !isLoading ? <span className="text-xs text-fg-subtle">กำลังอัปเดต…</span> : null}
        </Toolbar>

        {isLoading ? (
          <TableSkeleton columns={5} />
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            icon={<Wallet size={28} />}
            title={search || categoryId ? 'ไม่พบค่าใช้จ่ายที่ค้นหา' : 'ยังไม่มีรายการค่าใช้จ่าย'}
            description={search || categoryId ? undefined : 'บันทึกรายจ่ายรายการแรกของคุณ'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>รายการ</th>
                    <th>ผู้ขาย</th>
                    <th>หมวดหมู่</th>
                    <th>วันที่</th>
                    <th className="text-right">ยอดรวม</th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((expense) => (
                    <tr key={expense.id}>
                      <td className="font-medium text-fg">{expense.title}</td>
                      <td className="text-fg-muted">{expense.supplierName || '-'}</td>
                      <td className="text-fg-muted">{expense.categoryName || '-'}</td>
                      <td className="tabular text-fg-muted">{formatDate(expense.date)}</td>
                      <td className="tabular text-right text-fg">{formatMoney(expense.totalAmount)}</td>
                      <td>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label="แก้ไข"
                            onClick={() => {
                              setEditing(expense);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label="ลบ"
                            onClick={() => setDeleting(expense)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              pageSize={data.pageSize}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <ExpenseFormDialog open={formOpen} onClose={() => setFormOpen(false)} expense={editing} />

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => void handleDelete()}
        title="ลบค่าใช้จ่าย"
        message={`ต้องการลบ "${deleting?.title}" ใช่หรือไม่ การลบไม่สามารถย้อนกลับได้`}
        confirmLabel="ลบ"
        loading={deleteExpense.isPending}
      />
    </div>
  );
}
